import { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useParams, Link } from 'react-router-dom';
import cn from 'classnames';
import { ReactComponent as MessageDoubleCheckIcon } from 'assets/img/message-double-check.svg';
import { ReactComponent as MessageSingleCheckIcon } from 'assets/img/message-single-check.svg';
import { useStores } from 'stores';
import UserAvatarCircle from 'components/UserAvatarCircle/UserAvatarCircle';
import ChatMessageCounter from './components/ChatMessageCounter';
import { getLastMsgDate } from '../../../helpers/utils';
import styles from './ChatCard.module.scss';

const ChatCard = observer(({ conversation }) => {
  const {
    messageStore,
    themeStore,
    userStore: { userProfile },
  } = useStores();
  const { id } = useParams();

  const {
    userFirstName,
    userLastName,
    lastMessageAdded,
    lastMessageId,
    theyLastReadMessageId,
    lastWroteUserId,
    lastText,
  } = conversation;

  const isLastFromMe = userProfile?.id && lastWroteUserId && lastWroteUserId === userProfile?.id;
  const isReadByThem = isLastFromMe ? lastMessageId === theyLastReadMessageId : false;

  const trimText = (text, maxLength = 26) => {
    if (text.length > maxLength) {
      return `${text.slice(0, maxLength).trim()} …`;
    }
    return text;
  };

  const lastMsgDate = useMemo(() => (lastMessageAdded ? getLastMsgDate(lastMessageAdded) : null), [lastMessageAdded]);

  const getChatCardFooter = useCallback(() => {
    const draft = sessionStorage.getItem(`draftMsg${conversation.id}`);
    if (draft && conversation.id !== Number(messageStore.currentConversation)) {
      return (
        <p className={styles['message-text']}>
          <span className={styles['chat-draft-msg']}>Draft: </span>
          <span className={styles['message-preview-text']}>{`${trimText(JSON.parse(draft).message, 19)}`}</span>
        </p>
      );
    }

    return (
      <p className={cn(styles['message-text'], styles['message-preview'])}>
        <span className={styles['message-status']} aria-hidden>
          {isLastFromMe ? (isReadByThem ? <MessageDoubleCheckIcon /> : <MessageSingleCheckIcon />) : null}
        </span>
        <span className={styles['message-preview-text']}>{trimText(lastText ?? '')}</span>
      </p>
    );
  }, [conversation, messageStore, isLastFromMe, isReadByThem]);

  return (
    <div className={styles['chat-card-wrapper']}>
      <Link
        to={`/conversation/${conversation.id}`}
        className={cn(styles['chat-card'], { [styles['selected']]: Number(id) === conversation.id })}
      >
        <div className={styles['main-text']}>
          <div className={styles['chart-card-text-wrapper']}>
              <UserAvatarCircle
                className={styles['chat-avatar']}
                firstName={userFirstName}
                lastName={userLastName}
                size={48}
              />
            <div className={styles['chart-card-message']}>
              <div className={styles['chat-card-title']}>
                <p className={styles['chart-card-name']}>{`${userFirstName} ${userLastName}`}</p>
              </div>
              {getChatCardFooter()}
            </div>
            {lastMsgDate && <div className={styles['chat-last-message-time']}>{lastMsgDate}</div>}
            <ChatMessageCounter
              className={styles['chat-card-message-counter']}
              unread={isLastFromMe ? 0 : messageStore.unreadConversations[conversation.id]}
              themeMode={themeStore.themeMode}
            />
          </div>
        </div>
      </Link>
    </div>
  );
});
export default ChatCard;
